// Selector compacto de variante: la ficha con lo que tiene un solo valor y, por cada dimensión con
// dos o más opciones, su título y debajo una fila de chips con scroll horizontal.
import { cn } from '@/lib/cn';

export interface VariantChipOption {
    key: string;
    label: string;
    selected: boolean;
    /** La combinación existe con lo ya elegido. */
    available: boolean;
    inStock: boolean;
}

export interface VariantChipDimension {
    id: string;
    label: string;
    options: readonly VariantChipOption[];
}

function chipName(option: VariantChipOption, allowSoldOut: boolean): string {
    if (!option.available) return `${option.label} (no disponible)`;
    if (!option.inStock) return `${option.label} (${allowSoldOut ? 'sin piezas' : 'agotado'})`;
    return option.label;
}

export function VariantPicker({
    summary,
    dimensions,
    onSelect,
    allowSoldOut = false,
}: {
    summary: string;
    dimensions: readonly VariantChipDimension[];
    onSelect: (key: string) => void;
    /** Lo agotado se marca «sin piezas» pero se puede elegir (p. ej. para cotizar). */
    allowSoldOut?: boolean;
}) {
    return (
        <div className="flex flex-col gap-1">
            {summary !== '' && (
                <span
                    data-testid="variant-summary"
                    className="truncate text-[12px] text-muted-foreground"
                >
                    {summary}
                </span>
            )}
            {dimensions.map((dimension) => (
                <div key={dimension.id} className="flex min-w-0 flex-col gap-0.5">
                    <span className="text-[11px] text-muted-foreground">{dimension.label}</span>
                    <div
                        role="group"
                        aria-label={dimension.label}
                        className="flex min-w-0 flex-nowrap items-center gap-1 overflow-x-auto pb-0.5"
                    >
                        {dimension.options.map((option) => {
                            const name = chipName(option, allowSoldOut);
                            const disabled =
                                !option.available || (!option.inStock && !allowSoldOut);
                            return (
                                <button
                                    key={option.key}
                                    type="button"
                                    aria-pressed={option.selected}
                                    aria-label={name}
                                    title={name}
                                    disabled={disabled}
                                    onClick={() => {
                                        onSelect(option.key);
                                    }}
                                    className={cn(
                                        'h-7 flex-none whitespace-nowrap rounded-md border px-2 text-[12px] tabular-nums transition-colors',
                                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                                        option.selected
                                            ? 'border-foreground bg-muted font-semibold text-foreground'
                                            : 'border-border bg-card text-foreground hover:border-muted-foreground',
                                        !option.inStock && 'text-muted-foreground line-through',
                                        !option.available && 'border-dashed opacity-50',
                                        disabled && 'cursor-not-allowed hover:border-border',
                                    )}
                                >
                                    {option.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
            ))}
        </div>
    );
}
